import {
  createInterviewBaseQuery,
  HTTP_METHODS,
  INTERVIEW,
  INTERVIEW_ROUTES,
} from "@cointervue/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import type {
  CreateRoomRequest,
  CreateRoomResponse,
  FeedbackScreenResponse,
  InterviewStep0Response,
  InterviewStep1Response,
  ValidateInterviewResponse,
  ConversationHistoryResponse,
  PanelistNameApiRequest,
  ConvertCodeRequest,
  ConvertCodeResponse,
} from "./types";

export const interviewApi = createApi({
  reducerPath: "interviewApi",
  baseQuery: createInterviewBaseQuery(INTERVIEW.href),
  tagTypes: ["Interview"],
  endpoints: (builder) => ({
    verifyInterviewToken: builder.mutation<
      ValidateInterviewResponse,
      { token: string }
    >({
      query: (token) => ({
        url: INTERVIEW_ROUTES.verifyInterviewToken,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: token,
      }),
      invalidatesTags: [{ type: "Interview", id: "LIST" }],
    }),
    getInterviewStep0: builder.query<InterviewStep0Response, void>({
      query: () => ({
        url: INTERVIEW_ROUTES.preInterviewDetails,
        method: HTTP_METHODS.GET,
        params: { step: 0 },
      }),
    }),

    getInterviewStep1: builder.query<InterviewStep1Response, void>({
      query: () => ({
        url: INTERVIEW_ROUTES.preInterviewDetails,
        method: HTTP_METHODS.GET,
        params: { step: 1 },
      }),
    }),

    interviewFeedback: builder.mutation<void, { rating: number }>({
      query: (rating) => ({
        url: INTERVIEW_ROUTES.interviewFeedback,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: rating,
      }),
    }),

    createRoom: builder.mutation<CreateRoomResponse, CreateRoomRequest>({
      query: (params) => ({
        url: INTERVIEW_ROUTES.createRoom,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        params,
      }),
    }),

    endCall: builder.mutation<void, void>({
      query: () => ({
        url: INTERVIEW_ROUTES.endCall,
        method: HTTP_METHODS.PATCH,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
    }),

    feedbackScreen: builder.query<FeedbackScreenResponse, void>({
      query: () => ({
        url: INTERVIEW_ROUTES.feedbackScreen,
        method: HTTP_METHODS.GET,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
    }),
    getConversationHistory: builder.query<
      ConversationHistoryResponse,
      { sessionId: string }
    >({
      query: ({ sessionId }) => ({
        url: INTERVIEW_ROUTES.getConversation,
        method: HTTP_METHODS.GET,
        params: { sessionId },
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
    }),

    tabChange: builder.mutation<void, void>({
      query: () => ({
        url: INTERVIEW_ROUTES.tabChange,
        method: HTTP_METHODS.PATCH,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
    }),
    panelistName: builder.mutation<void, PanelistNameApiRequest>({
      query: (body) => ({
        url: INTERVIEW_ROUTES.panelistName,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body
      }),
    }),
    submitProofs: builder.mutation<void, FormData>({
      query: (body) => ({
        url: INTERVIEW_ROUTES.candidateProofs,
        method: HTTP_METHODS.POST,
        body,
      }),
    }),
    convertCode: builder.query<ConvertCodeResponse, ConvertCodeRequest>({
      query: ({ language }) => ({
        url: INTERVIEW_ROUTES.convertCode,
        method: HTTP_METHODS.GET,
        params: { language },
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
    }),
  }),
});

export const {
  useVerifyInterviewTokenMutation,
  useLazyGetInterviewStep0Query,
  useLazyGetInterviewStep1Query,
  useInterviewFeedbackMutation,
  useCreateRoomMutation,
  useEndCallMutation,
  useFeedbackScreenQuery,
  useGetConversationHistoryQuery,
  useTabChangeMutation,
  usePanelistNameMutation,
  useSubmitProofsMutation,
  useLazyConvertCodeQuery,
} = interviewApi;
